/**
 * Data loaders for the shared WhatsApp message builders.
 *
 * Every function takes the Supabase client as a parameter so the browser
 * (user session) and the WhatsApp bot (service role) run the same queries.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { GameMessageGame, GameMessageInput } from './gameMessage';
import type { TeamMessagePlayer } from './teamsMessage';
import type { GameSummary } from './postMatchSummary';
import type { PostMatchInsight, ConfidenceThreshold } from './insights';

const WHATSAPP_MEMBER_VALUES = ['Yes', 'Proxy'];

/** Same shape the admin GameList loads for GameCard. */
export const GAME_MESSAGE_SELECT = `
  id,
  date,
  status,
  max_players,
  sequence_number,
  registration_window_end,
  venue:venues(name, google_maps_url),
  game_registrations(
    status,
    selection_method,
    using_token,
    team,
    player:players!game_registrations_player_id_fkey(id, friendly_name, unpaid_games)
  )
`;

export async function loadGameForMessage(client: SupabaseClient, gameId: string): Promise<GameMessageGame> {
  const { data, error } = await client
    .from('games')
    .select(GAME_MESSAGE_SELECT)
    .eq('id', gameId)
    .single();

  if (error) throw new Error(`Failed to load game ${gameId}: ${error.message}`);
  return data as unknown as GameMessageGame;
}

/**
 * Loads everything buildGameMessage() needs beyond the game itself.
 *
 * Throws when a query the message cannot do without fails (group members, XP).
 * Other failures degrade the message and are reported through onWarning,
 * matching the admin GameCard's original behaviour.
 */
export async function loadGameMessageInput(
  client: SupabaseClient,
  game: GameMessageGame,
  onWarning: (message: string, error: unknown) => void = () => {},
): Promise<GameMessageInput> {
  const { data: players, error: playersError } = await client
    .from('players')
    .select('id, friendly_name, whatsapp_group_member')
    .in('whatsapp_group_member', WHATSAPP_MEMBER_VALUES)
    .order('friendly_name');

  if (playersError) throw new Error(`Failed to get player data: ${playersError.message}`);

  const { data: shieldUsageRows, error: shieldError } = await client
    .from('shield_token_usage')
    .select(`
      player_id,
      players!shield_token_usage_player_id_fkey(
        friendly_name,
        protected_streak_value
      )
    `)
    .eq('game_id', game.id);

  if (shieldError) onWarning('Failed to get shield token usage', shieldError);

  const { data: playersWithShields, error: playersWithShieldsError } = await client
    .from('players')
    .select('id, friendly_name, shield_tokens_available')
    .in('whatsapp_group_member', WHATSAPP_MEMBER_VALUES)
    .gt('shield_tokens_available', 0)
    .order('friendly_name');

  if (playersWithShieldsError) onWarning('Failed to get players with shield tokens', playersWithShieldsError);

  const { data: injuredPlayers, error: injuredPlayersError } = await client
    .from('players')
    .select('id, friendly_name')
    .in('whatsapp_group_member', WHATSAPP_MEMBER_VALUES)
    .eq('injury_token_active', true)
    .order('friendly_name');

  if (injuredPlayersError) onWarning('Failed to get injured players', injuredPlayersError);

  // The view holds the eligibility rules (recent participation, not recently
  // selected, no outstanding payments).
  const { data: tokenStatusData, error: tokenStatusError } = await client
    .from('public_player_token_status')
    .select('player_id, is_eligible');

  if (tokenStatusError) onWarning('Failed to get token status data', tokenStatusError);

  const eligibleIds = new Set(
    (tokenStatusData || []).filter(ts => ts.is_eligible).map(ts => ts.player_id as string)
  );
  const tokenEligibleNames = (players || [])
    .filter(p => eligibleIds.has(p.id))
    .map(p => p.friendly_name as string);

  const registeredIds = (game.game_registrations || [])
    .map(reg => reg.player?.id)
    .filter((id): id is string => Boolean(id));

  const { data: playerXpData, error: xpError } = await client
    .from('player_xp')
    .select('player_id, xp')
    .in('player_id', registeredIds);

  if (xpError) throw new Error(`Failed to get player XP data: ${xpError.message}`);

  let tokenCooldownPlayerIds = new Set<string>();
  let noShowPenaltyPlayerIds = new Set<string>();
  if (game.status === 'players_announced') {
    const [{ data: tokenCooldownData }, { data: noShowData }] = await Promise.all([
      client.rpc('check_previous_game_token_usage', { current_game_id: game.id }),
      client.rpc('get_no_show_penalised_players', { p_game_id: game.id }),
    ]);
    tokenCooldownPlayerIds = new Set(
      (tokenCooldownData || []).map((u: { player_id: string }) => u.player_id)
    );
    noShowPenaltyPlayerIds = new Set(
      (noShowData || []).map((u: { player_id: string }) => u.player_id)
    );
  }

  return {
    game,
    tokenEligibleNames,
    playersWithShields: (playersWithShields || []) as GameMessageInput['playersWithShields'],
    injuredNames: (injuredPlayers || []).map(p => p.friendly_name as string),
    shieldUsage: (shieldUsageRows || []).map((row: any) => ({
      friendly_name: row.players?.friendly_name,
      protected_streak_value: row.players?.protected_streak_value,
    })),
    xpByPlayerId: new Map((playerXpData || []).map(({ player_id, xp }) => [player_id as string, xp as number])),
    tokenCooldownPlayerIds,
    noShowPenaltyPlayerIds,
  };
}

/** Team lists for buildTeamsGroupMessage(), with permanent keepers flagged. */
export async function loadTeamsForMessage(
  client: SupabaseClient,
  gameId: string,
): Promise<{ orangeTeam: TeamMessagePlayer[]; blueTeam: TeamMessagePlayer[] }> {
  const [registrations, keepers] = await Promise.all([
    client
      .from('game_registrations')
      .select('team, player:players!game_registrations_player_id_fkey(id, friendly_name)')
      .eq('game_id', gameId)
      .eq('status', 'selected'),
    client
      .from('permanent_goalkeepers')
      .select('player_id')
      .eq('game_id', gameId),
  ]);

  if (registrations.error) throw new Error(`Failed to load teams: ${registrations.error.message}`);
  if (keepers.error) throw new Error(`Failed to load permanent goalkeepers: ${keepers.error.message}`);

  const keeperIds = new Set((keepers.data || []).map(k => k.player_id as string));
  const toPlayer = (reg: any): TeamMessagePlayer => ({
    friendly_name: reg.player?.friendly_name || 'Unknown',
    isPermanentGK: keeperIds.has(reg.player?.id),
  });

  const rows = registrations.data || [];
  return {
    orangeTeam: rows.filter(r => r.team === 'orange').map(toPlayer),
    blueTeam: rows.filter(r => r.team === 'blue').map(toPlayer),
  };
}

export interface PostMatchData {
  game: GameSummary;
  insights: PostMatchInsight[];
  thresholds: Record<string, ConfidenceThreshold>;
}

export function toPostMatchInsight(row: {
  id: string;
  analysis_type: string;
  priority: number;
  headline: string;
  details: Record<string, unknown> | null;
  player_ids: string[] | null;
  created_at: string;
}): PostMatchInsight {
  return {
    id: row.id,
    analysisType: row.analysis_type,
    priority: row.priority,
    headline: row.headline,
    details: row.details || {},
    playerIds: row.player_ids || [],
    createdAt: row.created_at,
  };
}

export function toThresholdMap(
  rows: Array<{ insight_category: string; low_threshold: number; high_threshold: number }> | null,
): Record<string, ConfidenceThreshold> {
  const thresholdMap: Record<string, ConfidenceThreshold> = {};
  (rows || []).forEach(row => {
    thresholdMap[row.insight_category] = {
      insightCategory: row.insight_category,
      lowThreshold: row.low_threshold,
      highThreshold: row.high_threshold,
    };
  });
  return thresholdMap;
}

export async function loadPostMatchData(client: SupabaseClient, gameId: string): Promise<PostMatchData> {
  const [insightsResult, gameResult, thresholdsResult] = await Promise.all([
    client.rpc('get_post_match_analysis', { p_game_id: gameId }),
    client.from('games').select('sequence_number, score_blue, score_orange, outcome').eq('id', gameId).single(),
    client.rpc('get_confidence_thresholds'),
  ]);

  if (insightsResult.error) throw new Error(`Failed to load post-match analysis: ${insightsResult.error.message}`);
  if (gameResult.error) throw new Error(`Failed to load game ${gameId}: ${gameResult.error.message}`);

  return {
    game: {
      sequenceNumber: gameResult.data.sequence_number,
      scoreBlue: gameResult.data.score_blue ?? 0,
      scoreOrange: gameResult.data.score_orange ?? 0,
      outcome: gameResult.data.outcome as GameSummary['outcome'],
    },
    insights: (insightsResult.data || []).map(toPostMatchInsight),
    thresholds: toThresholdMap(thresholdsResult.data),
  };
}
